import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { Grid, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { API } from 'aws-amplify';
import { config } from '../../conf/amplify';

import Loader from '../../components/Loader';
import QuotesForm from '../../components/Forms/QuotesForms/QuotesForm';
import { getQuotesLaunched } from '../../store/reducer';
import { switchAdminData } from '../../utils/services/formats';
import QuotesHeader from '../../components/Quotes/QuotesHeader';
import CustomButton from '../../components/Button';
import styles from './styles';

export const CotationSummary = (props) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const classes = styles();

  useEffect(() => {
    if (id) {
      dispatch(getQuotesLaunched({ quoteId: id }))
    }
  }, [id]);

  const { cotationData, medicActsData, getQuotesLoading, getMedicActsLoading, currentQuoteData, user } = useSelector((state) => ({
    cotationData: state.getIn(['store', 'cotationData']),
    medicActsData: state.getIn(['store', 'medicActsData']),
    getMedicActsLoading: state.getIn(['store', 'getMedicActsLoading']),
    getQuotesLoading: state.getIn(['store', 'getQuotesLoading']),
    currentQuoteData: state.getIn(['store', 'currentQuoteData']),
    user: state.getIn(['app', 'userDynamo', 'user']),
  }));

  const PATIENT = cotationData?.context;
  const actIds = React.useMemo(() => {
    const actsList = currentQuoteData?.medicActs?.map(timeframe => timeframe?.acts || []).flat(1) || cotationData?.medicActs?.map(timeframe => timeframe?.acts || []).flat(1);
    const packageList = currentQuoteData?.medicActs?.map(timeframe => timeframe?.packageSession || []).flat(1) || cotationData?.medicActs?.map(timeframe => timeframe?.package || []).flat(1);
    return actsList?.concat(packageList);
  }, [currentQuoteData, cotationData]);
  const [acts, setActs] = useState();

  const [reportOpen, setReportOpen] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [reportSending, setReportSending] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const [reportError, setReportError] = useState('');

  const handleOpenReport = () => {
    setReportOpen(true);
    setReportMessage('');
    setReportSent(false);
    setReportError('');
  };

  const handleCloseReport = () => {
    setReportOpen(false);
  };

  const handleSendReport = async () => {
    if (!reportMessage.trim()) return;
    setReportSending(true);
    setReportError('');
    try {
      const customHeader = {
        'Content-Type': 'application/json',
        'x-api-key': config.apiKey
      };
      
      const body = {
        type: "DEVIS_ERROR_MESSAGE",
        devisId: id,
        message: reportMessage,
        user: {
          id: user?.id,
          email: user?.email,
          firstName: user?.firstName,
          lastName: user?.lastName,
        }
      };

      await API.post(config.apiGateway.NAME, '/messages', {
        headers: customHeader,
        body
      });

      setReportSent(true);
    } catch (err) {
      console.error(err);
      setReportError("Une erreur est survenue lors de l'envoi de votre message. Veuillez réessayer.");
    } finally {
      setReportSending(false);
    }
  };

  useEffect(() => {
    let acts = [];
    if (actIds && medicActsData) {
      actIds?.map((actID) => {
        const act = medicActsData?.find(actData => actData.id === actID);
        if (act) {
          let currentAct = {};
          let instructionsArray = [];
          if (act.bulles) {
            act.bulles.map((bulle) => {
              instructionsArray.push(bulle)
            })
          }
          currentAct.instructions = instructionsArray;
          currentAct.medicalAct = act.medicalAct;
          acts.push(currentAct);
        }
      })
    }
    const cleanActs = Array.from(new Set(acts.map(JSON.stringify))).map(JSON.parse);
    setActs(cleanActs)
  }, [medicActsData, actIds]);

  return (
    <main>
      <div className={classes.toolbar} />
      <QuotesHeader title="Résultat de cotations optimisées des actes de la journée pour votre patient" isSummary />
      {getMedicActsLoading || getQuotesLoading
        ? <Grid container alignItems="center" className={classes.loaderWrapper}><Loader /></Grid>
        : <Grid container>
          <Box p={1}>
            <Formik>
              {(props) => <QuotesForm
                editable={false}
                results={cotationData?.medicActs}
                {...props}>
                {cotationData && (
                  <Box p={2}>
                    <Typography variant="h1">Contexte de soins</Typography>
                    <ul className={classes.actsList}>
                      <li><Typography>Age : {PATIENT?.age}</Typography></li>
                      <li><Typography>Lieu : {switchAdminData(PATIENT?.location) || PATIENT?.location}</Typography></li>
                      <li><Typography>Sexe : {switchAdminData(PATIENT?.sex) || PATIENT?.sex}</Typography></li>
                      {PATIENT?.patientType && <li><Typography>Type de patient : {PATIENT?.patientType?.map((x, key) => `${switchAdminData(x) || x}${key < PATIENT?.patientType?.length - 1 ? ', ' : ''}`)}</Typography></li>}
                    </ul>

                    <Typography variant="h1">Instructions</Typography>
                    {acts?.length > 0
                      ? <ul className={classes.actsList}>
                        {acts?.map((act, actKey) => <Box my={2} key={actKey}>
                          {act?.instructions.length > 0 &&
                            <li>
                              <Typography className={classes.actLabel}> {act.medicalAct} :</Typography>
                            </li>
                          }
                          <Box className={classes.instructionsBox}>
                            {act?.instructions?.map((instruction, key) =>
                              <Typography key={key}> {instruction}</Typography>
                            )}
                          </Box>
                        </Box>)}
                      </ul>
                      : <Typography>Pas d'instructions spécifiques</Typography>}
                    <Box mt={3} display="flex" justifyContent="flex-start">
                      <CustomButton
                        title="Signaler une erreur"
                        variant="outlined"
                        color="secondary"
                        handleClick={handleOpenReport}
                      />
                    </Box>
                  </Box>
                )}
              </QuotesForm>}
            </Formik>
          </Box>
        </Grid>
      }

      <Dialog
        open={reportOpen}
        onClose={handleCloseReport}
        maxWidth="sm"
        fullWidth
        disableBackdropClick={reportSending}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" style={{ fontWeight: 'bold' }}>
              Signaler une erreur dans le devis
            </Typography>
            <IconButton onClick={handleCloseReport} disabled={reportSending} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {reportSent ? (
            <Box my={2} p={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: 4 }}>
              <Typography variant="h6" align="center" style={{ fontWeight: '500' }}>
                Votre signalement a bien été envoyé. Merci !
              </Typography>
            </Box>
          ) : (
            <>
              <input type="hidden" name="devisId" value={id || ''} />
              <TextField
                label="Votre message"
                placeholder="Veuillez décrire précisément l'erreur constatée dans ce devis..."
                value={reportMessage}
                onChange={(e) => setReportMessage(e.target.value)}
                fullWidth
                multiline
                rows={4}
                margin="normal"
                variant="outlined"
                required
                disabled={reportSending}
              />
              {reportError && (
                <Box my={2} p={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: 4 }}>
                  <Typography variant="body2">{reportError}</Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          {reportSent ? (
            <CustomButton
              title="Fermer"
              handleClick={handleCloseReport}
              variant="contained"
              color="primary"
            />
          ) : (
            <>
              <CustomButton
                title="Annuler"
                handleClick={handleCloseReport}
                disabled={reportSending}
                variant="outlined"
                color="secondary"
              />
              <Box ml={2}>
                <CustomButton
                  title="Envoyer"
                  handleClick={handleSendReport}
                  loading={reportSending}
                  disabled={!reportMessage.trim() || reportSending}
                  variant="contained"
                  color="primary"
                />
              </Box>
            </>
          )}
        </DialogActions>
      </Dialog>
    </main>
  )


};

export default CotationSummary;
